import { z } from "zod";

export type PaginatedResponse<T> = {
  items: T[];
  total: number;
  page: number;
  page_size: number;
};

export type OuraAdminStatus = {
  user: {
    id: number;
    username: string;
    roles: string[];
  };
};

export type OuraOperation = {
  id: number;
  resourceType: string;
  actionType: string;
  status: string;
  signature: string | null;
  collection: string | null;
  tokenMint: string | null;
  asset: string | null;
  vaultAta: string | null;
  requestSummary: Record<string, unknown>;
  responseSummary: Record<string, unknown>;
  actorUserId: number | null;
  createdAt: string;
};

export type OuraProject = {
  // Core project data
  projectId: number;
  projectTitle: string;
  projectDescription: string | null;
  projectImage: string | null;
  projectNftCollection: string | null;
  projectStartDate: string;
  projectEndDate: string;
  projectIsActive: boolean | null;
  projectIsVipSession: boolean | null;
  projectIsEggMechanism: boolean | null;
  // Partner data
  partnerId: number;
  partnerName: string;
  partnerSlug: string;
  partnerLogo: string;
  // Token data
  tokenMint: string | null;
  tokenName: string | null;
  tokenDecimals: number | null;
  // Egg data
  eggId: number | null;
  eggName: string | null;
  eggMinted: number | null;
  eggMaxMinted: number | null;
  // NFT aggregates
  nftCount: number;
  nftCollections: string[];
  totalMaxMinted: number;
  // Trait aggregates
  traitCount: number;
  traitCollections: string[];
  // Oura admin overlay data
  ouraCollection: string | null;
  ouraTokenMint: string | null;
  ouraAdmin: string | null;
  ouraMaxSupply: string | null;
  ouraReleased: string | null;
  ouraUserMinted: string | null;
  ouraAdminMinted: string | null;
  ouraLastSignature: string | null;
  ouraCollectionName: string | null;
  ouraCollectionUri: string | null;
  ouraRoyaltyRecipients: string[] | null;
  ouraRoyaltySharesBps: number[] | null;
  ouraUpdatedAt: string | null;
  resolvedCollection?: string | null;
  resolvedTokenMint?: string | null;
  nftCollectionDetails?: OuraProjectCollection[];
};

export type OuraProjectCollection = {
  collection: string;
  collectionName: string | null;
  nftNames: string[];
  eggNames: string[];
  nftCount: number;
  totalMaxMinted: number;
  isTimelineCollection: boolean;
};

export type OuraOnchainProject = {
  admin: string;
  token_mint: string;
  mint_decimals: number;
  max_supply: string;
  released: string;
  max_mint_per_user: string;
  max_mint_per_vip_user: string;
  admin_minted: string;
  user_minted: string;
  vesting_mode: string;
  vesting_unlock_ts: string;
  mint_nft_fee_lamports: string;
  update_nft_fee_lamports: string;
  mint_price_total: string;
  escrow_amount: string;
  num_revenue_wallets: number;
  revenue_wallets: string[];
  revenue_shares: string[];
};

export type OuraProjectDetail = {
  project: OuraProject & {
    resolvedCollection: string | null;
    resolvedTokenMint: string | null;
  };
};

export type OuraProjectCollectionDetail = {
  project: OuraProject & {
    resolvedCollection: string | null;
    resolvedTokenMint: string | null;
  };
  onchain: OuraOnchainProject | null;
  onchain_error: string | null;
};

export type OuraTrait = {
  id: number;
  collection: string;
  authority: string | null;
  maxSupply: string | null;
  userMinted: string | null;
  mintFeeLamports: string | null;
  updateFeeLamports: string | null;
  royaltyRecipients: string[];
  royaltySharesBps: number[];
  traitName: string | null;
  traitUri: string | null;
  lastSignature: string | null;
  createdAt: string;
  updatedAt: string;
  traitCount?: number;
  traitNames?: string[];
  partnerNames?: string[];
  categoryNames?: string[];
  rarityNames?: string[];
  images?: string[];
};

export type OuraTraitItem = {
  id: number;
  idPublic: string;
  name: string;
  image: string;
  price: number;
  mintFee: string;
  protocolFee: string;
  traitCollection: string | null;
  partnerId: number;
  partnerName: string;
  partnerSlug: string;
  categoryId: number;
  categoryName: string;
  rarityId: number;
  rarityName: string;
  rarityTitle: string;
  createdAt: string;
  updatedAt: string;
};

export type OuraTraitDetail = {
  trait: OuraTrait;
  traitItems: OuraTraitItem[];
};

export type OuraOnchainTrait = {
  authority: string;
  max_supply: string;
  user_minted: string;
  mint_fee_lamports: string;
  update_fee_lamports: string;
};

export type OuraProtocolConfig = {
  authority: string;
  treasury: string;
};

export type OuraVaultInfo = {
  owner: string;
  amount: string;
  is_unlocked: boolean;
  bump: number;
};

export type OuraTransactionResult = {
  signature: string;
  collection?: string;
  asset?: string;
  vault_ata?: string;
};

export type OuraListParams = {
  page?: number;
  page_size?: number;
  search?: string;
  sort?: string;
  order?: "asc" | "desc";
  resource_type?: string;
  action_type?: string;
  status?: string;
};

const secretKeyArrayString = z.string().min(2, "Secret key array JSON is required").superRefine((value, ctx) => {
  let parsed: unknown;

  try {
    parsed = JSON.parse(value);
  } catch {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      message: "Use valid JSON for the secretKey array",
    });
    return;
  }

  if (!Array.isArray(parsed)) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      message: "Paste only the secretKey array, not an object with publicKey",
    });
    return;
  }

  const isValidByteArray = parsed.length > 0 && parsed.every((item) =>
    Number.isInteger(item) && item >= 0 && item <= 255
  );

  if (!isValidByteArray) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      message: "secretKey must be an array of byte numbers",
    });
  }
});
const addressString = z.string().min(32, "Address is required").max(64);
const optionalAddressString = z.preprocess(
  (value) => (value === "" ? undefined : value),
  addressString.optional(),
);
const bigintText = z.string().regex(/^\d+$/, "Use a non-negative integer");
const uriString = z.string().url("Use a valid URL");
const optionalText = z.preprocess(
  (value) => (value === "" ? undefined : value),
  z.string().min(1).optional(),
);
const optionalUriString = z.preprocess(
  (value) => (value === "" ? undefined : value),
  uriString.optional(),
);

export const protocolInitSchema = z.object({ treasury: addressString });
export const protocolUpdateSchema = z.object({
  new_authority_wallet: addressString,
  new_treasury_wallet: addressString,
});
export const protocolAmountSchema = z.object({ amount: bigintText });
export const protocolWithdrawSchema = z.object({
  treasury: addressString,
  amount: bigintText,
});

export const royaltySchema = z.object({
  recipients: z.string().optional(),
  sharesBps: z.string().optional(),
});

export const projectSchema = z.object({
  admin: secretKeyArrayString,
  collection: optionalAddressString,
  token_mint: addressString,
  max_supply: bigintText,
  released: bigintText,
  max_mint_per_user: bigintText,
  max_mint_per_vip_user: bigintText,
  vesting_mode: z.enum(["None", "Permanent", "Switch", "TimeStamp"]),
  vesting_unlock_ts: bigintText,
  mint_nft_fee_lamports: bigintText,
  update_nft_fee_lamports: bigintText,
  mint_price_total: bigintText,
  escrow_amount: bigintText,
  revenue_wallets: z.string().min(1, "Add at least one revenue wallet"),
  revenue_shares: z.string().min(1, "Add matching revenue shares"),
  royalty_recipients: z.string().min(1, "Add at least one royalty recipient"),
  royalty_shares_bps: z.string().min(1, "Add matching royalty shares"),
  collection_name: z.string().min(1),
  collection_uri: uriString,
});

export const nftMutationSchema = z.object({
  keypair: secretKeyArrayString,
  collection: addressString,
  token_mint: addressString,
  asset: optionalAddressString,
  recipient: optionalAddressString,
  name: optionalText,
  uri: optionalUriString,
  confirmed: z.boolean().optional(),
});

export const traitSchema = z.object({
  collection: optionalAddressString,
  max_supply: bigintText,
  mint_fee_lamports: bigintText,
  update_fee_lamports: bigintText,
  royalty_recipients: z.string().min(1, "Add at least one royalty recipient"),
  royalty_shares_bps: z.string().min(1, "Add matching royalty shares"),
  trait_name: z.string().min(1),
  trait_uri: uriString,
});

export const traitNftSchema = z.object({
  payer: secretKeyArrayString,
  collection: addressString,
  asset: optionalAddressString,
  name: optionalText,
  uri: optionalUriString,
  confirmed: z.boolean().optional(),
});

export const vaultTransferSchema = z.object({
  payer: secretKeyArrayString,
  collection: addressString,
  asset: addressString,
  project_token_mint: addressString,
  new_token_mint: addressString,
  amount: bigintText,
});

export function splitCsv(value: string): string[] {
  return value
    .split(",")
    .map((item) => item.trim())
    .filter(Boolean);
}

const vestingModeValues: Record<z.infer<typeof projectSchema>["vesting_mode"], number> = {
  None: 0,
  Permanent: 1,
  Switch: 2,
  TimeStamp: 3,
};

export function buildProjectPayload(values: z.infer<typeof projectSchema>) {
  return {
    ...values,
    vesting_mode: vestingModeValues[values.vesting_mode],
    revenue_wallets: splitCsv(values.revenue_wallets),
    revenue_shares: splitCsv(values.revenue_shares),
    num_revenue_wallets: splitCsv(values.revenue_wallets).length,
    royalty_recipients: splitCsv(values.royalty_recipients),
    royalty_shares_bps: splitCsv(values.royalty_shares_bps).map(Number),
    num_royalty_recipients: splitCsv(values.royalty_recipients).length,
  };
}

export function buildTraitPayload(values: z.infer<typeof traitSchema>) {
  return {
    ...values,
    royalty_recipients: splitCsv(values.royalty_recipients),
    royalty_shares_bps: splitCsv(values.royalty_shares_bps).map(Number),
    num_royalty_recipients: splitCsv(values.royalty_recipients).length,
  };
}
